import React, { useState } from 'react';
import { Trash2, Copy, Eye, Code, Plus, Download, Upload } from 'lucide-react';

const AppBuilder = () => {
    const [components, setComponents] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [previewMode, setPreviewMode] = useState(false);
    const [draggedItem, setDraggedItem] = useState(null);
    const [resizing, setResizing] = useState(null);
    const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const [dragging, setDragging] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    const componentLibrary = [
        { type: 'button', label: 'Button', icon: '🔘' },
        { type: 'text', label: 'Text', icon: '📝' },
        { type: 'input', label: 'Input', icon: '📥' },
        { type: 'textarea', label: 'Textarea', icon: '📄' },
        { type: 'image', label: 'Image', icon: '🖼️' },
        { type: 'container', label: 'Container', icon: '📦' },
        { type: 'heading', label: 'Heading', icon: '📰' },
        { type: 'checkbox', label: 'Checkbox', icon: '☑️' },
        { type: 'radio', label: 'Radio', icon: '🔘' },
        { type: 'select', label: 'Select', icon: '📋' },
        { type: 'link', label: 'Link', icon: '🔗' },
        { type: 'divider', label: 'Divider', icon: '➖' },
    ];

    const addComponent = (type) => {
        const newComponent = {
            id: Date.now(),
            type,
            props: getDefaultProps(type),
            position: { x: 50, y: 50 },
        };
        setComponents([...components, newComponent]);
        setSelectedId(newComponent.id);
    };

    const getDefaultProps = (type) => {
        const defaults = {
            button: {
                text: 'Click Me',
                bgColor: '#3b82f6',
                textColor: '#ffffff',
                padding: '10px 20px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer',
                width: 'auto',
                margin: '0px'
            },
            text: {
                content: 'Sample text',
                fontSize: '16px',
                color: '#000000',
                fontWeight: '400',
                textAlign: 'left',
                lineHeight: '1.5',
                margin: '0px',
                padding: '0px'
            },
            input: {
                placeholder: 'Enter text...',
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                bgColor: '#ffffff',
                textColor: '#000000'
            },
            textarea: {
                placeholder: 'Enter text...',
                width: '100%',
                height: '100px',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                bgColor: '#ffffff',
                textColor: '#000000',
                resize: 'vertical'
            },
            image: {
                src: 'https://via.placeholder.com/300x200',
                width: '300px',
                height: '200px',
                borderRadius: '0px',
                objectFit: 'cover',
                border: 'none'
            },
            container: {
                bgColor: '#f3f4f6',
                padding: '20px',
                borderRadius: '8px',
                minHeight: '100px',
                border: '2px dashed #d1d5db',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
            },
            heading: {
                text: 'Heading',
                level: 'h1',
                color: '#000000',
                fontWeight: '700',
                margin: '0px',
                textAlign: 'left'
            },
            checkbox: {
                label: 'Checkbox Label',
                checked: false,
                fontSize: '14px',
                color: '#000000'
            },
            radio: {
                label: 'Radio Label',
                name: 'radio-group',
                checked: false,
                fontSize: '14px',
                color: '#000000'
            },
            select: {
                options: 'Option 1,Option 2,Option 3',
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                bgColor: '#ffffff'
            },
            link: {
                text: 'Link Text',
                href: '#',
                color: '#3b82f6',
                fontSize: '14px',
                textDecoration: 'underline',
                fontWeight: '400'
            },
            divider: {
                height: '1px',
                bgColor: '#e5e7eb',
                width: '100%',
                margin: '20px 0px'
            }
        };
        return defaults[type] || {};
    };

    const updateComponentProp = (id, key, value) => {
        setComponents(components.map(c =>
            c.id === id ? { ...c, props: { ...c.props, [key]: value } } : c
        ));
    };

    const deleteComponent = (id) => {
        setComponents(components.filter(c => c.id !== id));
        if (selectedId === id) setSelectedId(null);
    };

    const duplicateComponent = (id) => {
        const comp = components.find(c => c.id === id);
        if (comp) {
            const newComp = {
                ...comp,
                id: Date.now(),
                position: { x: comp.position.x + 20, y: comp.position.y + 20 }
            };
            setComponents([...components, newComp]);
        }
    };

    const moveComponent = (id, direction) => {
        const index = components.findIndex(c => c.id === id);
        if (direction === 'up' && index > 0) {
            const newComps = [...components];
            [newComps[index], newComps[index - 1]] = [newComps[index - 1], newComps[index]];
            setComponents(newComps);
        } else if (direction === 'down' && index < components.length - 1) {
            const newComps = [...components];
            [newComps[index], newComps[index + 1]] = [newComps[index + 1], newComps[index]];
            setComponents(newComps);
        }
    };

    const clearAll = () => {
        if (window.confirm('Are you sure you want to clear all components?')) {
            setComponents([]);
            setSelectedId(null);
        }
    };

    const exportJSON = () => {
        const dataStr = JSON.stringify(components, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
        const exportFileDefaultName = 'app-builder-export.json';
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
    };

    const importJSON = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const imported = JSON.parse(event.target.result);
                    setComponents(imported);
                    setSelectedId(null);
                } catch (err) {
                    alert('Invalid JSON file');
                }
            };
            reader.readAsText(file);
        }
    };

    const onDragStart = (e, index) => {
        setDraggedItem(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const onDragOver = (e, index) => {
        e.preventDefault();
        if (draggedItem === null || draggedItem === index) return;

        const newComps = [...components];
        const draggedComp = newComps[draggedItem];
        newComps.splice(draggedItem, 1);
        newComps.splice(index, 0, draggedComp);

        setComponents(newComps);
        setDraggedItem(index);
    };

    const onDragEnd = () => {
        setDraggedItem(null);
    };

    const startResize = (e, compId, dimension) => {
        e.stopPropagation();
        e.preventDefault();
        const comp = components.find(c => c.id === compId);
        if (!comp) return;

        setResizing({ id: compId, dimension });
        setResizeStart({
            x: e.clientX,
            y: e.clientY,
            width: parseInt(comp.props.width) || 0,
            height: parseInt(comp.props.height) || 0,
        });
    };

    const handleResize = (e) => {
        if (!resizing) return;

        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;

        setComponents(components.map(c => {
            if (c.id === resizing.id) {
                const newProps = { ...c.props };

                if (resizing.dimension === 'width' || resizing.dimension === 'both') {
                    const newWidth = Math.max(50, resizeStart.width + deltaX);
                    newProps.width = newWidth + 'px';
                }

                if (resizing.dimension === 'height' || resizing.dimension === 'both') {
                    const newHeight = Math.max(50, resizeStart.height + deltaY);
                    newProps.height = newHeight + 'px';
                }

                return { ...c, props: newProps };
            }
            return c;
        }));
    };

    const stopResize = () => {
        setResizing(null);
    };

    const startDrag = (e, compId) => {
        if (resizing) return;
        const comp = components.find(c => c.id === compId);
        if (!comp) return;

        setDragging(compId);
        setDragOffset({
            x: e.clientX - comp.position.x,
            y: e.clientY - comp.position.y,
        });
    };

    const handleDrag = (e) => {
        if (!dragging) return;

        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;

        setComponents(components.map(c =>
            c.id === dragging
                ? { ...c, position: { x: Math.max(0, newX), y: Math.max(0, newY) } }
                : c
        ));
    };

    const stopDrag = () => {
        setDragging(null);
    };

    React.useEffect(() => {
        if (resizing) {
            document.addEventListener('mousemove', handleResize);
            document.addEventListener('mouseup', stopResize);
            return () => {
                document.removeEventListener('mousemove', handleResize);
                document.removeEventListener('mouseup', stopResize);
            };
        }
    }, [resizing, resizeStart, components]);

    React.useEffect(() => {
        if (dragging) {
            document.addEventListener('mousemove', handleDrag);
            document.addEventListener('mouseup', stopDrag);
            return () => {
                document.removeEventListener('mousemove', handleDrag);
                document.removeEventListener('mouseup', stopDrag);
            };
        }
    }, [dragging, dragOffset, components]);

    const renderComponent = (comp, isPreview = false, index = 0) => {
        const isSelected = !isPreview && selectedId === comp.id;
        const canResizeWidth = ['button', 'input', 'textarea', 'image', 'container', 'select'].includes(comp.type);
        const canResizeHeight = ['textarea', 'image', 'container'].includes(comp.type);

        const baseStyle = {
            position: 'absolute',
            left: comp.position.x + 'px',
            top: comp.position.y + 'px',
            outline: isSelected ? '2px solid #3b82f6' : 'none',
            outlineOffset: '2px',
            cursor: isPreview ? 'default' : (dragging === comp.id ? 'grabbing' : 'grab'),
            transition: dragging === comp.id ? 'none' : 'all 0.2s',
            minWidth: '50px',
            zIndex: isSelected ? 100 : 1,
        };

        const componentMap = {
            button: (
                <button style={{
                    backgroundColor: comp.props.bgColor,
                    color: comp.props.textColor,
                    padding: comp.props.padding,
                    borderRadius: comp.props.borderRadius,
                    border: comp.props.border,
                    cursor: comp.props.cursor,
                    fontSize: comp.props.fontSize,
                    fontWeight: comp.props.fontWeight,
                    width: comp.props.width,
                    margin: comp.props.margin,
                }}>
                    {comp.props.text}
                </button>
            ),
            text: (
                <p style={{
                    fontSize: comp.props.fontSize,
                    color: comp.props.color,
                    fontWeight: comp.props.fontWeight,
                    textAlign: comp.props.textAlign,
                    lineHeight: comp.props.lineHeight,
                    margin: comp.props.margin,
                    padding: comp.props.padding,
                }}>
                    {comp.props.content}
                </p>
            ),
            input: (
                <input
                    type="text"
                    placeholder={comp.props.placeholder}
                    style={{
                        width: comp.props.width,
                        padding: comp.props.padding,
                        borderRadius: comp.props.borderRadius,
                        border: comp.props.border,
                        fontSize: comp.props.fontSize,
                        backgroundColor: comp.props.bgColor,
                        color: comp.props.textColor,
                    }}
                />
            ),
            textarea: (
                <textarea
                    placeholder={comp.props.placeholder}
                    style={{
                        width: comp.props.width,
                        height: comp.props.height,
                        padding: comp.props.padding,
                        borderRadius: comp.props.borderRadius,
                        border: comp.props.border,
                        fontSize: comp.props.fontSize,
                        backgroundColor: comp.props.bgColor,
                        color: comp.props.textColor,
                        resize: comp.props.resize,
                        fontFamily: 'inherit',
                    }}
                />
            ),
            image: (
                <img
                    src={comp.props.src}
                    alt="Component"
                    style={{
                        width: comp.props.width,
                        height: comp.props.height,
                        borderRadius: comp.props.borderRadius,
                        objectFit: comp.props.objectFit,
                        border: comp.props.border,
                        display: 'block',
                    }}
                />
            ),
            container: (
                <div style={{
                    backgroundColor: comp.props.bgColor,
                    padding: comp.props.padding,
                    borderRadius: comp.props.borderRadius,
                    minHeight: comp.props.minHeight,
                    border: comp.props.border,
                    width: comp.props.width,
                    display: comp.props.display,
                    flexDirection: comp.props.flexDirection,
                    gap: comp.props.gap,
                }}>
                    <span style={{ color: '#9ca3af', fontSize: '14px' }}>Container - Drop components here</span>
                </div>
            ),
            heading: (
                React.createElement(comp.props.level, {
                    style: {
                        color: comp.props.color,
                        margin: comp.props.margin,
                        fontWeight: comp.props.fontWeight,
                        textAlign: comp.props.textAlign,
                    }
                }, comp.props.text)
            ),
            checkbox: (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        defaultChecked={comp.props.checked}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: comp.props.fontSize, color: comp.props.color }}>
                        {comp.props.label}
                    </span>
                </label>
            ),
            radio: (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                        type="radio"
                        name={comp.props.name}
                        defaultChecked={comp.props.checked}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: comp.props.fontSize, color: comp.props.color }}>
                        {comp.props.label}
                    </span>
                </label>
            ),
            select: (
                <select style={{
                    width: comp.props.width,
                    padding: comp.props.padding,
                    borderRadius: comp.props.borderRadius,
                    border: comp.props.border,
                    fontSize: comp.props.fontSize,
                    backgroundColor: comp.props.bgColor,
                    cursor: 'pointer',
                }}>
                    {(comp.props.options || 'Option 1,Option 2,Option 3').split(',').map((opt, i) => (
                        <option key={i} value={opt.trim()}>{opt.trim()}</option>
                    ))}
                </select>
            ),
            link: (
                <a
                    href={comp.props.href}
                    style={{
                        color: comp.props.color,
                        fontSize: comp.props.fontSize,
                        textDecoration: comp.props.textDecoration,
                        fontWeight: comp.props.fontWeight,
                        cursor: 'pointer',
                    }}
                >
                    {comp.props.text}
                </a>
            ),
            divider: (
                <hr style={{
                    height: comp.props.height,
                    backgroundColor: comp.props.bgColor,
                    border: 'none',
                    width: comp.props.width,
                    margin: comp.props.margin,
                }} />
            ),
        };

        return (
            <div
                key={comp.id}
                style={baseStyle}
                onMouseDown={(e) => {
                    e.stopPropagation();
                    if (!isPreview) {
                        setSelectedId(comp.id);
                        startDrag(e, comp.id);
                    }
                }}
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                {!isPreview && isSelected && (
                    <>
                        <div style={{
                            position: 'absolute',
                            top: '-32px',
                            right: '0',
                            display: 'flex',
                            gap: '4px',
                            backgroundColor: '#3b82f6',
                            padding: '4px',
                            borderRadius: '4px',
                            zIndex: 10,
                        }}>
                            <button onClick={() => moveComponent(comp.id, 'up')} style={iconBtnStyle} title="Move Up">↑</button>
                            <button onClick={() => moveComponent(comp.id, 'down')} style={iconBtnStyle} title="Move Down">↓</button>
                            <button onClick={() => duplicateComponent(comp.id)} style={iconBtnStyle} title="Duplicate">
                                <Copy size={14} />
                            </button>
                            <button onClick={() => deleteComponent(comp.id)} style={iconBtnStyle} title="Delete">
                                <Trash2 size={14} />
                            </button>
                        </div>

                        {/* Resize Handles */}
                        {canResizeWidth && (
                            <div
                                onMouseDown={(e) => startResize(e, comp.id, canResizeHeight ? 'both' : 'width')}
                                style={{
                                    position: 'absolute',
                                    right: '-4px',
                                    top: canResizeHeight ? '50%' : '0',
                                    transform: canResizeHeight ? 'translateY(-50%)' : 'none',
                                    width: '8px',
                                    height: canResizeHeight ? '40px' : '100%',
                                    backgroundColor: '#3b82f6',
                                    cursor: 'ew-resize',
                                    borderRadius: '4px',
                                    zIndex: 10,
                                }}
                                title="Resize width"
                            />
                        )}

                        {canResizeHeight && (
                            <div
                                onMouseDown={(e) => startResize(e, comp.id, 'height')}
                                style={{
                                    position: 'absolute',
                                    bottom: '-4px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: '40px',
                                    height: '8px',
                                    backgroundColor: '#3b82f6',
                                    cursor: 'ns-resize',
                                    borderRadius: '4px',
                                    zIndex: 10,
                                }}
                                title="Resize height"
                            />
                        )}

                        {canResizeWidth && canResizeHeight && (
                            <div
                                onMouseDown={(e) => startResize(e, comp.id, 'both')}
                                style={{
                                    position: 'absolute',
                                    right: '-4px',
                                    bottom: '-4px',
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: '#3b82f6',
                                    cursor: 'nwse-resize',
                                    borderRadius: '2px',
                                    zIndex: 11,
                                }}
                                title="Resize both"
                            />
                        )}
                    </>
                )}
                {componentMap[comp.type]}
            </div>
        );
    };

    const iconBtnStyle = {
        background: 'transparent',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    const selectedComponent = components.find(c => c.id === selectedId);

    const renderPropertyInput = (key, value, compId) => {
        const inputStyle = {
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #d1d5db',
            fontSize: '14px',
            width: '100%',
        };

        if (key === 'level') {
            return (
                <select
                    value={value}
                    onChange={(e) => updateComponentProp(compId, key, e.target.value)}
                    style={inputStyle}
                >
                    <option value="h1">H1</option>
                    <option value="h2">H2</option>
                    <option value="h3">H3</option>
                    <option value="h4">H4</option>
                    <option value="h5">H5</option>
                    <option value="h6">H6</option>
                </select>
            );
        }

        if (key === 'textAlign') {
            return (
                <select
                    value={value}
                    onChange={(e) => updateComponentProp(compId, key, e.target.value)}
                    style={inputStyle}
                >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                    <option value="justify">Justify</option>
                </select>
            );
        }

        if (key === 'fontWeight') {
            return (
                <select
                    value={value}
                    onChange={(e) => updateComponentProp(compId, key, e.target.value)}
                    style={inputStyle}
                >
                    <option value="300">Light</option>
                    <option value="400">Normal</option>
                    <option value="500">Medium</option>
                    <option value="600">Semi Bold</option>
                    <option value="700">Bold</option>
                </select>
            );
        }

        if (key === 'objectFit') {
            return (
                <select
                    value={value}
                    onChange={(e) => updateComponentProp(compId, key, e.target.value)}
                    style={inputStyle}
                >
                    <option value="cover">Cover</option>
                    <option value="contain">Contain</option>
                    <option value="fill">Fill</option>
                    <option value="none">None</option>
                </select>
            );
        }

        if (key === 'resize') {
            return (
                <select
                    value={value}
                    onChange={(e) => updateComponentProp(compId, key, e.target.value)}
                    style={inputStyle}
                >
                    <option value="none">None</option>
                    <option value="vertical">Vertical</option>
                    <option value="horizontal">Horizontal</option>
                    <option value="both">Both</option>
                </select>
            );
        }

        if (key === 'flexDirection') {
            return (
                <select
                    value={value}
                    onChange={(e) => updateComponentProp(compId, key, e.target.value)}
                    style={inputStyle}
                >
                    <option value="row">Row</option>
                    <option value="column">Column</option>
                </select>
            );
        }

        if (key === 'checked') {
            return (
                <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => updateComponentProp(compId, key, e.target.checked)}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
            );
        }

        if (key.includes('Color') || key.includes('color') || key === 'bgColor') {
            return (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                        type="color"
                        value={value}
                        onChange={(e) => updateComponentProp(compId, key, e.target.value)}
                        style={{ width: '50px', height: '38px', cursor: 'pointer', border: '1px solid #d1d5db', borderRadius: '4px' }}
                    />
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => updateComponentProp(compId, key, e.target.value)}
                        style={{ ...inputStyle, width: 'auto', flex: 1 }}
                    />
                </div>
            );
        }

        if (key === 'options') {
            return (
                <textarea
                    value={value}
                    onChange={(e) => updateComponentProp(compId, key, e.target.value)}
                    placeholder="Option 1,Option 2,Option 3"
                    style={{ ...inputStyle, minHeight: '60px', fontFamily: 'inherit' }}
                />
            );
        }

        return (
            <input
                type="text"
                value={value}
                onChange={(e) => updateComponentProp(compId, key, e.target.value)}
                style={inputStyle}
            />
        );
    };

    return (
        <div style={{ display: 'flex', height: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            {/* Sidebar - Component Library */}
            <div style={{ width: '250px', backgroundColor: '#f9fafb', borderRight: '1px solid #e5e7eb', padding: '20px', overflowY: 'auto' }}>
                <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>Components</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {componentLibrary.map(comp => (
                        <button
                            key={comp.type}
                            onClick={() => addComponent(comp.type)}
                            style={{
                                padding: '12px',
                                backgroundColor: 'white',
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.borderColor = '#3b82f6';
                                e.target.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.borderColor = '#e5e7eb';
                                e.target.style.transform = 'translateY(0)';
                            }}
                        >
                            <span style={{ fontSize: '20px' }}>{comp.icon}</span>
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>{comp.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Canvas */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    gap: '10px',
                }}>
                    <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>App Builder</h1>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <label style={{
                            padding: '8px 16px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                        }}>
                            <Upload size={16} />
                            Import
                            <input type="file" accept=".json" onChange={importJSON} style={{ display: 'none' }} />
                        </label>
                        <button
                            onClick={exportJSON}
                            disabled={components.length === 0}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: components.length === 0 ? '#9ca3af' : '#8b5cf6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: components.length === 0 ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '14px',
                                fontWeight: '500',
                            }}
                        >
                            <Download size={16} />
                            Export
                        </button>
                        <button
                            onClick={clearAll}
                            disabled={components.length === 0}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: components.length === 0 ? '#9ca3af' : '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: components.length === 0 ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '14px',
                                fontWeight: '500',
                            }}
                        >
                            <Trash2 size={16} />
                            Clear
                        </button>
                        <button
                            onClick={() => setPreviewMode(!previewMode)}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: previewMode ? '#f59e0b' : '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '14px',
                                fontWeight: '500',
                            }}
                        >
                            {previewMode ? <Code size={16} /> : <Eye size={16} />}
                            {previewMode ? 'Edit' : 'Preview'}
                        </button>
                    </div>
                </div>
                <div style={{
                    flex: 1,
                    padding: '40px',
                    overflowY: 'auto',
                    backgroundColor: '#ffffff',
                    position: 'relative',
                }}
                    onClick={() => !previewMode && setSelectedId(null)}
                >
                    {components.length === 0 ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: '#9ca3af',
                            gap: '12px',
                        }}>
                            <Plus size={48} />
                            <p style={{ fontSize: '18px', margin: 0, fontWeight: '500' }}>Add components to get started</p>
                            <p style={{ fontSize: '14px', margin: 0 }}>Click on any component from the left sidebar</p>
                        </div>
                    ) : (
                        <div style={{ position: 'relative', minHeight: '800px' }}>
                            {components.map((comp, index) => renderComponent(comp, previewMode, index))}
                        </div>
                    )}
                </div>
            </div>

            {/* Properties Panel */}
            {!previewMode && (
                <div style={{
                    width: '320px',
                    backgroundColor: '#f9fafb',
                    borderLeft: '1px solid #e5e7eb',
                    padding: '20px',
                    overflowY: 'auto',
                }}>
                    <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
                        {selectedComponent ? 'Properties' : 'No Selection'}
                    </h2>
                    {selectedComponent ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{
                                padding: '12px',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                            }}>
                                {selectedComponent.type}
                            </div>
                            {Object.entries(selectedComponent.props).map(([key, value]) => (
                                <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: '#374151',
                                        textTransform: 'capitalize',
                                    }}>
                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                    </label>
                                    {renderPropertyInput(key, value, selectedComponent.id)}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '200px',
                            color: '#9ca3af',
                            gap: '12px',
                        }}>
                            <div style={{ fontSize: '48px' }}>⚙️</div>
                            <p style={{ fontSize: '14px', textAlign: 'center', margin: 0 }}>
                                Select a component to edit its properties
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AppBuilder;
